import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart3,
  TrendingUp,
  Users,
  Eye,
  Globe,
  Calendar,
  Download,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";
import { AppLayout } from "@/components/AppLayout";

const AnalyticsPage1 = () => {
  const [events, setEvents] = useState([]);
  const [timeRange, setTimeRange] = useState("7d");
  const [selectedEvent, setSelectedEvent] = useState("all");

  useEffect(() => {
    // Load events for analytics
    const savedEvents = localStorage.getItem('generatedEvents');
    if (savedEvents) {
      setEvents(JSON.parse(savedEvents));
    }
  }, []);

  // Mock analytics data
  const analyticsData = {
    totalViews: 3847,
    uniqueVisitors: 2156,
    averageTime: "2:34",
    bounceRate: "32%",
    conversionRate: "8.5%",
    topCountries: [
      { name: "United States", visits: 1247, percentage: 32.4 },
      { name: "United Kingdom", visits: 892, percentage: 23.2 },
      { name: "Canada", visits: 567, percentage: 14.7 },
      { name: "Germany", visits: 423, percentage: 11.0 },
      { name: "France", visits: 298, percentage: 7.7 }
    ],
    deviceStats: {
      mobile: 65,
      desktop: 28,
      tablet: 7
    },
    dailyViews: [
      { date: "2024-01-01", views: 234 },
      { date: "2024-01-02", views: 456 },
      { date: "2024-01-03", views: 378 },
      { date: "2024-01-04", views: 623 },
      { date: "2024-01-05", views: 542 },
      { date: "2024-01-06", views: 398 },
      { date: "2024-01-07", views: 445 }
    ]
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold gradient-text">Analytics Dashboard</h1>
          <p className="text-muted-foreground">
            Track performance and engagement for your event websites
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="glass-card border-0">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" className="glass-card border-0">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-full sm:w-48 glass-card border-0">
            <SelectValue placeholder="Select time range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="90d">Last 90 days</SelectItem>
            <SelectItem value="1y">Last year</SelectItem>
          </SelectContent>
        </Select>

        <Select value={selectedEvent} onValueChange={setSelectedEvent}>
          <SelectTrigger className="w-full sm:w-64 glass-card border-0">
            <SelectValue placeholder="Select event" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            {events.map((event: any) => (
              <SelectItem key={event.id} value={event.id}>
                {event.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="glass-card border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Views</p>
                <p className="text-2xl font-bold">{analyticsData.totalViews.toLocaleString()}</p>
                <p className="text-sm text-green-500 flex items-center gap-1">
                  <ArrowUpRight className="h-3 w-3" />
                  +12.5%
                </p>
              </div>
              <Eye className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Unique Visitors</p>
                <p className="text-2xl font-bold">{analyticsData.uniqueVisitors.toLocaleString()}</p>
                <p className="text-sm text-green-500 flex items-center gap-1">
                  <ArrowUpRight className="h-3 w-3" />
                  +8.2%
                </p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Time</p>
                <p className="text-2xl font-bold">{analyticsData.averageTime}</p>
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <ArrowDownRight className="h-3 w-3" />
                  -2.1%
                </p>
              </div>
              <Calendar className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Bounce Rate</p>
                <p className="text-2xl font-bold">{analyticsData.bounceRate}</p>
                <p className="text-sm text-green-500 flex items-center gap-1">
                  <ArrowDownRight className="h-3 w-3" />
                  -5.3%
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Conversion</p>
                <p className="text-2xl font-bold">{analyticsData.conversionRate}</p>
                <p className="text-sm text-green-500 flex items-center gap-1">
                  <ArrowUpRight className="h-3 w-3" />
                  +15.7%
                </p>
              </div>
              <Globe className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="glass-card">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="traffic">Traffic</TabsTrigger>
          <TabsTrigger value="engagement">Engagement</TabsTrigger>
          <TabsTrigger value="conversion">Conversion</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Traffic Chart Placeholder */}
          <Card className="glass-card border-0">
            <CardHeader>
              <CardTitle>Traffic Overview</CardTitle>
              <CardDescription>Daily visitor statistics for the selected period</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-center justify-center bg-muted/20 rounded-lg">
                <div className="text-center text-muted-foreground">
                  <BarChart3 className="h-12 w-12 mx-auto mb-4" />
                  <p>Traffic chart visualization coming soon</p>
                  <p className="text-sm">Integration with analytics providers in progress</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Geography and Device Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="glass-card border-0">
              <CardHeader>
                <CardTitle>Top Countries</CardTitle>
                <CardDescription>Visitor locations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {analyticsData.topCountries.map((country, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-theme-primary rounded-full"></div>
                        <span className="font-medium">{country.name}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-bold">{country.visits}</span>
                        <span className="text-sm text-muted-foreground ml-2">
                          {country.percentage}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-0">
              <CardHeader>
                <CardTitle>Device Types</CardTitle>
                <CardDescription>User device distribution</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Mobile</span>
                    <span className="font-bold">{analyticsData.deviceStats.mobile}%</span>
                  </div>
                  <div className="w-full bg-muted/30 rounded-full h-2">
                    <div
                      className="bg-theme-primary h-2 rounded-full"
                      style={{ width: `${analyticsData.deviceStats.mobile}%` }}
                    ></div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="font-medium">Desktop</span>
                    <span className="font-bold">{analyticsData.deviceStats.desktop}%</span>
                  </div>
                  <div className="w-full bg-muted/30 rounded-full h-2">
                    <div
                      className="bg-secondary h-2 rounded-full"
                      style={{ width: `${analyticsData.deviceStats.desktop}%` }}
                    ></div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="font-medium">Tablet</span>
                    <span className="font-bold">{analyticsData.deviceStats.tablet}%</span>
                  </div>
                  <div className="w-full bg-muted/30 rounded-full h-2">
                    <div
                      className="bg-theme-primary/10 hover:text-theme-primary h-2 rounded-full"
                      style={{ width: `${analyticsData.deviceStats.tablet}%` }}
                    ></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="traffic">
          <Card className="glass-card border-0">
            <CardHeader>
              <CardTitle>Traffic Sources</CardTitle>
              <CardDescription>Where your visitors are coming from</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-muted-foreground">
                <Globe className="h-12 w-12 mx-auto mb-4" />
                <p>Traffic source analytics coming soon...</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="engagement">
          <Card className="glass-card border-0">
            <CardHeader>
              <CardTitle>User Engagement</CardTitle>
              <CardDescription>How users interact with your event pages</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4" />
                <p>Engagement metrics coming soon...</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="conversion">
          <Card className="glass-card border-0">
            <CardHeader>
              <CardTitle>Conversion Tracking</CardTitle>
              <CardDescription>Event registration and conversion metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-muted-foreground">
                <TrendingUp className="h-12 w-12 mx-auto mb-4" />
                <p>Conversion tracking coming soon...</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>

  );
};

export default AnalyticsPage1;